/**
 * Email + password sign-in for external testers — the frontend half of the
 * backend's /api/auth/local/* routes.
 *
 * Staff sign in with Microsoft (MSAL / Entra). Testers have no Entra account,
 * so the backend issues them its own JWT, which every existing API route and
 * both WebSockets accept as a normal Bearer token. Rather than teach the ~20
 * `instance.acquireTokenSilent(...)` call sites about a second token source,
 * we reuse the devAuth trick: when a local session exists the MSAL instance is
 * patched so every acquireTokenSilent returns the local JWT and every account
 * lookup returns a synthetic account shaped like an Entra one. Nothing else in
 * the app needs to know which kind of principal is signed in.
 *
 * Session storage: localStorage key `localAuth` → { token, expiresAt, user }.
 * The token is short-lived by design (expires_in from the login response) and
 * any 401 from the API while a local session is active throws the tester back
 * to the login page — that is how a deactivated account is kicked out.
 *
 * Precedence: a dev token (devAuth) always wins over a local session.
 */
import type { AccountInfo, AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { apiScopes } from "./msalConfig";
import { isDevAuth } from "./devAuth";

const LS_KEY = "localAuth";
const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

// ── Contract types ───────────────────────────────────────────────────────────

export interface LocalUser {
  id: number | string;
  email: string;
  name: string;
  roles: string[];
  must_change_password: boolean;
}

export interface LocalLoginResponse {
  access_token: string;
  token_type: string;
  /** Seconds until the token expires. */
  expires_in: number;
  user: LocalUser;
}

export interface LocalMe {
  email: string;
  name: string;
  roles: string[];
  auth: "local" | "entra";
  must_change_password: boolean;
}

/** One row of GET /api/auth/local/users (staff only). */
export interface TesterRecord {
  id: number | string;
  email: string;
  name: string;
  is_active: boolean;
  must_change_password: boolean;
  created_at: string;
  created_by: string | null;
  last_login_at: string | null;
  locked_until: string | null;
}

export interface LocalSession {
  token: string;
  /** Epoch milliseconds. */
  expiresAt: number;
  user: LocalUser;
}

// ── Session storage ──────────────────────────────────────────────────────────

export function getLocalSession(): LocalSession | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<LocalSession>;
    if (!s || typeof s.token !== "string" || typeof s.expiresAt !== "number" || !s.user) return null;
    if (s.expiresAt <= Date.now()) {
      // Expired: treat exactly like signed out, and drop the stale record so
      // the next check is cheap.
      localStorage.removeItem(LS_KEY);
      return null;
    }
    return s as LocalSession;
  } catch {
    return null;
  }
}

export function setLocalSession(resp: LocalLoginResponse): LocalSession {
  const session: LocalSession = {
    token: resp.access_token,
    expiresAt: Date.now() + resp.expires_in * 1000,
    user: resp.user,
  };
  localStorage.setItem(LS_KEY, JSON.stringify(session));
  return session;
}

/** Patch the stored user (e.g. after a password change cleared the
 *  must_change_password flag) without touching the token. */
export function updateLocalUser(patch: Partial<LocalUser>): void {
  const s = getLocalSession();
  if (!s) return;
  localStorage.setItem(LS_KEY, JSON.stringify({ ...s, user: { ...s.user, ...patch } }));
}

export function clearLocalSession(): void {
  try { localStorage.removeItem(LS_KEY); } catch { /* nothing to clear */ }
}

/** True when the signed-in principal is an external tester. A dev token takes
 *  precedence, so this is false in dev-token mode even if a stale local
 *  session is lying around. */
export function isLocalAuth(): boolean {
  return !isDevAuth() && getLocalSession() !== null;
}

// ── Sign-out ─────────────────────────────────────────────────────────────────

let leaving = false;

/** Drop the session and go back to the login page. Idempotent: the first
 *  caller wins, later ones (a burst of 401s from parallel requests) no-op. */
export function signOutLocal(): void {
  if (leaving) return;
  leaving = true;
  clearLocalSession();
  location.assign("/");
}

// ── MSAL bypass ──────────────────────────────────────────────────────────────

function syntheticAccount(user: LocalUser): AccountInfo {
  const id = String(user.id);
  return {
    homeAccountId: `local:${id}`,
    environment: "local",
    tenantId: "local",
    username: user.email,
    localAccountId: id,
    name: user.name,
  };
}

/**
 * When a local session exists, override the MSAL instance so token acquisition
 * returns the local JWT and account lookups return the synthetic tester
 * account — covering every `instance.acquireTokenSilent(...)` call site
 * without touching any of them. Call AFTER applyDevAuth; a dev token wins.
 *
 * Also installs a fetch guard: any 401 from the API while the local session is
 * active means the token was revoked (tester deactivated/deleted) or expired
 * server-side, so the session is cleared and the app returns to the login page.
 */
export function applyLocalAuth(instance: IPublicClientApplication): void {
  if (isDevAuth()) return;
  const session = getLocalSession();
  if (!session) return;

  const account = syntheticAccount(session.user);

  const buildResult = (s: LocalSession): AuthenticationResult => ({
    accessToken: s.token,
    account,
    idToken: "",
    idTokenClaims: {},
    scopes: apiScopes,
    expiresOn: new Date(s.expiresAt),
    fromCache: true,
    tokenType: "Bearer",
    uniqueId: account.localAccountId,
    tenantId: "local",
    authority: "",
    correlationId: "local",
  } as unknown as AuthenticationResult);

  // Re-read the session on every call so a token that has expired since page
  // load is noticed at the next API call rather than silently sent and 401'd.
  const acquire = async (): Promise<AuthenticationResult> => {
    const current = getLocalSession();
    if (!current) {
      signOutLocal();
      // A full-page navigation to the login page is in flight; never hand the
      // caller an error it would render as a failure banner (same idea as
      // sessionGuard).
      return await new Promise<never>(() => {});
    }
    return buildResult(current);
  };

  const logout = async () => { signOutLocal(); };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const inst = instance as any;
  inst.acquireTokenSilent = acquire;
  inst.acquireTokenPopup = acquire;
  inst.acquireTokenRedirect = async () => { signOutLocal(); };
  inst.ssoSilent = acquire;
  inst.getAllAccounts = () => [account];
  inst.getActiveAccount = () => account;
  inst.getAccountByHomeId = () => account;
  inst.getAccountByLocalId = () => account;
  inst.getAccountByUsername = () => account;
  inst.loginRedirect = async () => { /* already signed in locally */ };
  inst.loginPopup = acquire;
  inst.logoutRedirect = logout;
  inst.logoutPopup = logout;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  installLocalFetchGuard();
}

let fetchGuardInstalled = false;

/** Turn "the API said 401 while a tester is signed in" into a clean return to
 *  the login page. Only patches window.fetch when a local session is active,
 *  and only looks at calls to our own API base. */
function installLocalFetchGuard(): void {
  if (fetchGuardInstalled) return;
  fetchGuardInstalled = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const resp = await original(input, init);
    if (resp.status === 401 && isApiUrl(input) && getLocalSession() !== null) {
      signOutLocal();
    }
    return resp;
  };
}

function isApiUrl(input: RequestInfo | URL): boolean {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  return !API_BASE || url.startsWith(API_BASE) || url.startsWith("/");
}

// ── API client for /api/auth/local/* ─────────────────────────────────────────

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/** The backend answers errors as `{"detail": "..."}`; fall back to the status
 *  line when the body is not that shape. */
async function readError(resp: Response): Promise<ApiError> {
  let detail = "";
  try {
    const body = await resp.json();
    if (typeof body?.detail === "string") detail = body.detail;
    else if (Array.isArray(body?.detail)) detail = body.detail.map((d: { msg?: string }) => d.msg ?? String(d)).join("; ");
  } catch { /* not JSON */ }
  return new ApiError(resp.status, detail || `${resp.status} ${resp.statusText || "Request failed"}`);
}

async function request<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const { token, ...rest } = init;
  const headers: Record<string, string> = { ...(rest.headers as Record<string, string> | undefined) };
  if (rest.body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const resp = await fetch(`${API_BASE}${path}`, { ...rest, headers });
  if (!resp.ok) throw await readError(resp);
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const localAuthApi = {
  login: (email: string, password: string) =>
    request<LocalLoginResponse>("/api/auth/local/login", {
      method: "POST", body: JSON.stringify({ email, password }),
    }),

  me: (token: string) =>
    request<LocalMe>("/api/auth/local/me", { token }),

  changePassword: (token: string, current_password: string, new_password: string) =>
    request<void>("/api/auth/local/change-password", {
      method: "POST", token, body: JSON.stringify({ current_password, new_password }),
    }),

  // Staff only — the caller passes whatever token the MSAL instance hands out
  // (Entra for staff, dev token for loop engineering).
  listTesters: (token: string) =>
    request<TesterRecord[]>("/api/auth/local/users", { token }),

  createTester: (token: string, email: string, name: string) =>
    request<{ user: TesterRecord; temporary_password: string }>("/api/auth/local/users", {
      method: "POST", token, body: JSON.stringify({ email, name }),
    }),

  resetTesterPassword: (token: string, id: TesterRecord["id"]) =>
    request<{ temporary_password: string }>(`/api/auth/local/users/${encodeURIComponent(String(id))}/reset-password`, {
      method: "POST", token,
    }),

  patchTester: (token: string, id: TesterRecord["id"], patch: { is_active?: boolean; name?: string }) =>
    request<TesterRecord>(`/api/auth/local/users/${encodeURIComponent(String(id))}`, {
      method: "PATCH", token, body: JSON.stringify(patch),
    }),

  deleteTester: (token: string, id: TesterRecord["id"]) =>
    request<void>(`/api/auth/local/users/${encodeURIComponent(String(id))}`, {
      method: "DELETE", token,
    }),
};

export const MIN_PASSWORD_LENGTH = 10;
